import React, { useEffect, useMemo, useState } from 'react';
import ProtocolUpgradeSDK from '../../utils/ProtocolUpgradeSDK';
import { useDevice } from '../../context/DeviceContext';
import {
  discoverFirmwareOnce,
  loadSelectedFirmwareWithFallback,
} from './firmwareLoader';
import { hasMcu2Chip } from './mcu2';
import {
  getFirmwareDisplayLabel,
  getFirmwareFiles,
} from './firmwareCatalog';
import {
  formatFirmwareVersion,
  getLatestFirmwareVersion,
  isFirmwareAtLatestVersion,
  isFirmwareUpdateAvailable,
  isWiredFirmwareConnection,
} from './upgradePlan';
import { getUpgradeProgressPercent } from './upgradeProgress';
import './FirmwareBinTester.css';

const emptyFirmwareState = (status = 'loading') => ({
  status,
  result: null,
  error: '',
});

const emptyUpgradeProgress = () => ({
  acknowledgedPackets: 0,
  packetCount: 0,
  bytesSent: 0,
  totalBytes: 0,
  retryCount: 0,
});

const toHex = (value, width = 8) =>
  `0x${Number(value).toString(16).padStart(width, '0').toUpperCase()}`;

const FirmwareBinTester = ({
  onUpgradeStart,
  onUpgradeSuccess,
  onUpgradeSuccessDismiss,
}) => {
  const {
    isConnected,
    connectionType,
    protocolVersion,
    sender,
    dataStore,
    deviceInfo,
  } = useDevice();
  const firmwareFiles = getFirmwareFiles(deviceInfo?.modelId);
  const hasFirmwareCatalog = firmwareFiles != null;
  const [deviceVersions, setDeviceVersions] = useState(() => {
    const parsed = dataStore?.getData('deviceConfig')?.parsed;
    return {
      softwareVersion: parsed?.softwareVersion || null,
      softwareVersionRaw: parsed?.softwareVersionRaw ?? null,
      mcu2ProductId: parsed?.mcu2ProductId ?? null,
      mcu2SoftwareVersion: parsed?.mcu2SoftwareVersion || null,
      mcu2SoftwareVersionRaw: parsed?.mcu2SoftwareVersionRaw ?? null,
    };
  });
  const [firmwares, setFirmwares] = useState({
    mcu1: emptyFirmwareState(),
    mcu2: emptyFirmwareState(),
  });
  const [upgradeState, setUpgradeState] = useState('idle');
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [upgradeStage, setUpgradeStage] = useState('');
  const [upgradeError, setUpgradeError] = useState('');
  const [currentUpgradeTarget, setCurrentUpgradeTarget] = useState('');
  const [progressByTarget, setProgressByTarget] = useState({
    mcu1: emptyUpgradeProgress(),
    mcu2: emptyUpgradeProgress(),
  });
  const upgradeSDK = useMemo(
    () => (sender ? new ProtocolUpgradeSDK(sender) : null),
    [sender]
  );

  useEffect(() => {
    if (!deviceInfo) return undefined;

    setUpgradeState('idle');
    setIsUpgradeDialogOpen(false);
    setUpgradeStage('');
    setUpgradeError('');

    if (!firmwareFiles) {
      setFirmwares({
        mcu1: emptyFirmwareState('unavailable'),
        mcu2: emptyFirmwareState('unavailable'),
      });
      return undefined;
    }

    setFirmwares({
      mcu1: emptyFirmwareState(),
      mcu2: emptyFirmwareState(),
    });

    let cancelled = false;

    const discoverFirmware = async (key) => {
      try {
        const result = await discoverFirmwareOnce(
          deviceInfo,
          key,
          firmwareFiles[key]
        );
        if (!cancelled) {
          setFirmwares((current) => ({
            ...current,
            [key]: { status: 'ready', result, error: '' },
          }));
        }
      } catch (loadError) {
        if (!cancelled) {
          setFirmwares((current) => ({
            ...current,
            [key]: {
              status: 'error',
              result: null,
              error:
                loadError?.message ||
                `${getFirmwareDisplayLabel(firmwareFiles[key])} 固件信息读取失败`,
            },
          }));
        }
      }
    };

    Object.keys(firmwareFiles).forEach(discoverFirmware);
    return () => {
      cancelled = true;
    };
  }, [deviceInfo, firmwareFiles]);

  useEffect(() => {
    if (!dataStore) return undefined;

    const updateDeviceVersions = (deviceConfig) => {
      const parsed = deviceConfig?.parsed;
      setDeviceVersions({
        softwareVersion: parsed?.softwareVersion || null,
        softwareVersionRaw: parsed?.softwareVersionRaw ?? null,
        mcu2ProductId: parsed?.mcu2ProductId ?? null,
        mcu2SoftwareVersion: parsed?.mcu2SoftwareVersion || null,
        mcu2SoftwareVersionRaw: parsed?.mcu2SoftwareVersionRaw ?? null,
      });
    };

    updateDeviceVersions(dataStore.getData('deviceConfig'));
    dataStore.on('deviceConfig', updateDeviceVersions);
    return () => dataStore.off('deviceConfig', updateDeviceVersions);
  }, [dataStore]);

  const isUpgrading = upgradeState === 'running';
  const isWired = isWiredFirmwareConnection(connectionType);
  const mcu2DetectionReady = deviceVersions.mcu2ProductId != null;
  const deviceHasMcu2 = hasMcu2Chip(deviceVersions.mcu2ProductId);
  const showMcu2Version = isConnected && deviceHasMcu2;
  const requiredFirmwareReady =
    hasFirmwareCatalog &&
    firmwares.mcu1.status === 'ready' &&
    (!deviceHasMcu2 || firmwares.mcu2.status === 'ready');
  const requiredFirmwareError =
    hasFirmwareCatalog
      ? firmwares.mcu1.error || (deviceHasMcu2 ? firmwares.mcu2.error : '')
      : '';
  const firmwareUpdates = [
    ...(deviceHasMcu2 &&
    isFirmwareUpdateAvailable(
      deviceVersions.mcu2SoftwareVersionRaw,
      firmwares.mcu2.result
    )
      ? ['mcu2']
      : []),
    ...(isFirmwareUpdateAvailable(
      deviceVersions.softwareVersionRaw,
      firmwares.mcu1.result
    )
      ? ['mcu1']
      : []),
  ];
  const needsUpgrade =
    isConnected &&
    mcu2DetectionReady &&
    requiredFirmwareReady &&
    firmwareUpdates.length > 0;
  const firmwareAtLatest =
    isConnected &&
    mcu2DetectionReady &&
    requiredFirmwareReady &&
    isFirmwareAtLatestVersion(
      deviceVersions.softwareVersionRaw,
      firmwares.mcu1.result
    ) &&
    (!deviceHasMcu2 ||
      isFirmwareAtLatestVersion(
        deviceVersions.mcu2SoftwareVersionRaw,
        firmwares.mcu2.result
      ));
  const upgradeTargets =
    firmwareUpdates.length > 0
      ? firmwareUpdates
      : firmwareAtLatest
        ? [...(deviceHasMcu2 ? ['mcu2'] : []), 'mcu1']
        : [];
  const canStartUpgrade =
    (needsUpgrade || firmwareAtLatest) &&
    isWired &&
    mcu2DetectionReady &&
    requiredFirmwareReady &&
    upgradeSDK != null &&
    !isUpgrading;

  const openUpgradeDialog = () => {
    if (!canStartUpgrade) return;
    setUpgradeState('idle');
    setUpgradeStage('');
    setUpgradeError('');
    setCurrentUpgradeTarget('');
    setProgressByTarget({
      mcu1: emptyUpgradeProgress(),
      mcu2: emptyUpgradeProgress(),
    });
    onUpgradeStart?.();
    setIsUpgradeDialogOpen(true);
  };

  const closeUpgradeDialog = () => {
    if (!isUpgrading) {
      if (upgradeState === 'success') onUpgradeSuccessDismiss?.();
      setIsUpgradeDialogOpen(false);
    }
  };

  const logPacket = (label, packet) => {
    const hex = Array.from(packet)
      .map((byte) => byte.toString(16).padStart(2, '0').toUpperCase())
      .join(' ');
    console.log(label, {
      length: packet.length,
      bytes: Array.from(packet),
      hex,
    });
  };

  const upgradeFirmwareTarget = async ({
    key,
    label,
    deviceType,
    firmware,
  }) => {
    const command = ProtocolUpgradeSDK.FIRMWARE_CONTROL_COMMAND;
    setCurrentUpgradeTarget(label);
    setProgressByTarget((current) => ({
      ...current,
      [key]: emptyUpgradeProgress(),
    }));

    console.group(
      `[${label} 固件更新] command=${command}, deviceType=${deviceType}`
    );
    console.log('[更新配置]', {
      target: label,
      command,
      deviceType,
      productId: toHex(firmware.header.hexPID),
      firmwareHeader: firmware.header,
      transferOrder: 'type=1 → type=2 → type=6 → command=30 → type=7',
    });

    try {
      setUpgradeStage(`${label}：读取设备升级信息`);
      const type1Request = ProtocolUpgradeSDK.buildUpgradeRequest({
        command,
        productId: firmware.header.hexPID,
        deviceType,
      });
      logPacket(`[TX ${label} type=1] 升级信息请求`, type1Request);
      const upgradeResponse = await upgradeSDK.sendUpgradeRequest({
        command,
        productId: firmware.header.hexPID,
        deviceType,
        timeout: 1500,
      });
      console.log(`[RX ${label} type=1]`, upgradeResponse);

      setUpgradeStage(`${label}：校验设备与固件`);
      const type2Request = ProtocolUpgradeSDK.buildVerificationInfoRequest({
        command,
        deviceType,
        mac: upgradeResponse.mac,
        aesKeys: firmware.header.aesKeys,
      });
      logPacket(`[TX ${label} type=2] 设备与固件校验请求`, type2Request);
      const verificationResponse =
        await upgradeSDK.sendVerificationInfoRequest({
          command,
          upgradeResponse,
          firmware,
          deviceType,
          timeout: 1500,
        });
      if (!verificationResponse.success) {
        throw new Error(
          `${label} 设备校验失败，状态码 ${verificationResponse.status}`
        );
      }

      setUpgradeStage(`${label}：准备未运行镜像`);
      const selectedTarget = ProtocolUpgradeSDK.selectInactiveAppImage(
        upgradeResponse,
        firmware
      );
      const type6Request = ProtocolUpgradeSDK.buildAppUpgradeRequest({
        command,
        deviceType,
        flashAddress: selectedTarget.flashAddress,
        size: selectedTarget.size,
        crc: selectedTarget.crc,
      });
      logPacket(`[TX ${label} type=6] APP 升级准备请求`, type6Request);
      const { target, appRNGResponse } =
        await upgradeSDK.sendAppUpgradeRequest({
          command,
          upgradeResponse,
          firmware,
          deviceType,
          timeout: 1500,
        });

      setUpgradeStage(`${label}：传输固件数据`);
      const firmwareResult = await upgradeSDK.sendFirmwareData({
        upgradeResponse,
        firmware,
        deviceType,
        ackTimeout: 100,
        maxRetries: 3,
        onProgress: (nextProgress) => {
          setProgressByTarget((current) => ({
            ...current,
            [key]: nextProgress,
          }));
        },
      });

      setUpgradeStage(`${label}：提交最终 APP 校验`);
      const type7Request = ProtocolUpgradeSDK.buildAppCheckRequest({
        command,
        deviceType,
        flag: upgradeResponse.dualImageModeRaw,
        appRNG: appRNGResponse.appRNG,
        appVersion: target.version,
      });
      logPacket(`[TX ${label} type=7] 最终 APP 校验请求`, type7Request);
      const appCheckResult = await upgradeSDK.sendAppCheckRequest({
        command,
        upgradeResponse,
        target,
        appRNGResponse,
        deviceType,
        timeout: 1500,
      });
      if (!appCheckResult.appCheckResponse.success) {
        throw new Error(
          `${label} 最终 APP 校验失败，状态码 ${appCheckResult.appCheckResponse.status}`
        );
      }

      return {
        key,
        label,
        deviceType,
        upgradeResponse,
        verificationResponse,
        target,
        appRNGResponse,
        firmwareResult,
        appCheckResponse: appCheckResult.appCheckResponse,
        checkData: appCheckResult.checkData,
      };
    } finally {
      console.groupEnd();
    }
  };

  const startUpgrade = async () => {
    if (!canStartUpgrade) return;
    if (!isWired) {
      setUpgradeError(
        connectionType === 'bluetooth'
          ? '蓝牙连接禁止版本更新，请改用 USB 有线连接'
          : '无线连接禁止版本更新，请改用 USB 有线连接'
      );
      setUpgradeState('error');
      return;
    }

    const orderDescription = upgradeTargets
      .map((key) => getFirmwareDisplayLabel(firmwareFiles[key]))
      .join(' → ');

    setUpgradeState('running');
    setUpgradeError('');

    try {
      const upgradePlan = [];
      for (const key of upgradeTargets) {
        const file = firmwareFiles[key];
        const displayLabel = getFirmwareDisplayLabel(file);
        setUpgradeStage(`${displayLabel}：读取并校验升级 BIN`);
        const firmware = await loadSelectedFirmwareWithFallback(
          file,
          firmwares[key].result
        );
        const currentVersion =
          key === 'mcu2'
            ? deviceVersions.mcu2SoftwareVersionRaw
            : deviceVersions.softwareVersionRaw;
        const canInstallFirmware =
          isFirmwareUpdateAvailable(currentVersion, firmware) ||
          isFirmwareAtLatestVersion(currentVersion, firmware);
        if (!canInstallFirmware) {
          throw new Error(`${displayLabel} 实际 BIN 版本低于设备当前版本`);
        }
        setFirmwares((current) => ({
          ...current,
          [key]: { status: 'ready', result: firmware, error: '' },
        }));
        upgradePlan.push({
          key,
          label: displayLabel,
          deviceType: file.mcuNumber - 1,
          firmware,
        });
      }

      const completedTargets = [];
      for (const target of upgradePlan) {
        // 必须串行等待；MCU2 完整成功后才允许开始 MCU1。
        completedTargets.push(await upgradeFirmwareTarget(target));
      }
      onUpgradeSuccess?.({
        targets: completedTargets.map((target) => ({
          key: target.key,
          label: target.label,
          packetCount: target.firmwareResult.packetCount,
        })),
      });
      setUpgradeStage(`${orderDescription} 版本更新完成`);
      setUpgradeState('success');
    } catch (upgradeFailure) {
      console.error('协议升级失败:', upgradeFailure);
      setUpgradeError(upgradeFailure?.message || '协议升级失败');
      setUpgradeStage('升级已停止');
      setUpgradeState('error');
    }
  };

  const renderLatestFirmwareVersion = (key) => {
    const file = firmwareFiles[key];
    const state = firmwares[key];
    const latestVersion = getLatestFirmwareVersion(state.result);

    return (
      <div className="firmware-latest-item" key={key}>
        <span>{getFirmwareDisplayLabel(file)} 最新固件版本</span>
        <strong>
          {state.status === 'ready'
            ? formatFirmwareVersion(latestVersion)
            : state.status === 'loading'
              ? '检查中…'
              : '--'}
        </strong>
      </div>
    );
  };

  return (
    <main className="firmware-tester">
      <div className="firmware-tester-header">
        <div>
          <span className="firmware-tester-eyebrow">Firmware update</span>
          <h1>设备协议版本更新</h1>
          <p>
            {hasFirmwareCatalog
              ? '仅支持 USB 有线更新，驱动会自动选择当前型号固件及升级顺序。'
              : '当前型号暂无可用更新文件，仅展示设备当前版本。'}
          </p>
        </div>
      </div>

      <section className="firmware-version-status">
        <div className="firmware-version-item">
          <span>软件版本</span>
          <strong>
            {isConnected ? deviceVersions.softwareVersion || '读取中…' : '--'}
          </strong>
        </div>
        {showMcu2Version && (
          <>
            <div className="firmware-version-divider" aria-hidden />
            <div className="firmware-version-item">
              <span>RF芯片软件版本</span>
              <strong>{deviceVersions.mcu2SoftwareVersion || '读取中…'}</strong>
            </div>
          </>
        )}
        <div className="firmware-version-divider" aria-hidden />
        <div className="firmware-version-item">
          <span>连接方式</span>
          <strong>
            {!isConnected
              ? '--'
              : isWired
                ? 'USB 有线'
                : connectionType === 'bluetooth'
                  ? '蓝牙（禁止更新）'
                  : '无线（禁止更新）'}
          </strong>
        </div>
        <div className="firmware-version-divider" aria-hidden />
        <div className="firmware-version-item">
          <span>协议版本</span>
          <strong>
            {isConnected ? protocolVersion || '读取中…' : '--'}
          </strong>
        </div>
      </section>

      {hasFirmwareCatalog && (
        <section className="firmware-latest-version">
          {deviceHasMcu2 && renderLatestFirmwareVersion('mcu2')}
          {renderLatestFirmwareVersion('mcu1')}
        </section>
      )}

      {requiredFirmwareError && (
        <section className="firmware-result-banner error" role="alert">
          <span className="firmware-result-mark">×</span>
          <div>
            <strong>升级文件信息不可用</strong>
            <p>{requiredFirmwareError}</p>
          </div>
        </section>
      )}

      {needsUpgrade && !isWired && (
        <section className="firmware-result-banner error" role="alert">
          <span className="firmware-result-mark">!</span>
          <div>
            <strong>无线连接禁止版本更新</strong>
            <p>
              {connectionType === 'bluetooth'
                ? '请改用 USB 线连接鼠标后重试。'
                : '请断开接收器并使用 USB 线连接鼠标后重试。'}
            </p>
          </div>
        </section>
      )}

      {((hasFirmwareCatalog && isConnected) || upgradeState !== 'idle') && (
        <section className="firmware-upgrade-card">
          <div className="firmware-upgrade-heading">
            <div>
              <span>设备协议升级</span>
              <strong>{isUpgrading ? upgradeStage : '版本更新'}</strong>
              <p>
                {!isConnected
                  ? '请先使用 USB 线连接设备。'
                  : !isWired
                    ? connectionType === 'bluetooth'
                      ? '当前为蓝牙连接，更新功能已禁用。'
                      : '当前为无线连接，更新功能已禁用。'
                    : !mcu2DetectionReady
                      ? '正在判断设备是否包含 RF芯片。'
                      : requiredFirmwareReady
                        ? firmwareUpdates.length > 1
                          ? '升级顺序：RF芯片 → USB芯片。'
                          : firmwareUpdates.length === 1
                            ? `仅需更新 ${getFirmwareDisplayLabel(
                                firmwareFiles[firmwareUpdates[0]]
                              )}。`
                            : '设备固件已是最新版本，可重新安装当前版本以测试升级流程。'
                        : '正在检查固件目录与文件名。'}
              </p>
            </div>
            <div className="firmware-upgrade-actions">
              <button
                type="button"
                className="firmware-upgrade-button"
                disabled={!canStartUpgrade}
                onClick={openUpgradeDialog}
              >
                {isUpgrading
                  ? `${currentUpgradeTarget} 更新中…`
                  : firmwareAtLatest
                    ? '重新安装当前版本'
                    : '开始版本更新'}
              </button>
            </div>
          </div>
        </section>
      )}

      {isUpgradeDialogOpen && (
        <div
          className="firmware-dialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeUpgradeDialog();
          }}
        >
          <section
            className="firmware-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="firmware-dialog-title"
          >
            <div className="firmware-dialog-header">
              <div>
                <span>设备升级</span>
                <h2 id="firmware-dialog-title">
                  {isUpgrading
                    ? '正在更新固件'
                    : upgradeState === 'success'
                      ? '版本更新完成'
                      : upgradeState === 'error'
                        ? '版本更新失败'
                        : firmwareAtLatest
                          ? '重新安装当前版本'
                          : '确认版本更新'}
                </h2>
              </div>
              {!isUpgrading && (
                <button
                  type="button"
                  className="firmware-dialog-close"
                  onClick={closeUpgradeDialog}
                  aria-label="关闭升级弹窗"
                >
                  ×
                </button>
              )}
            </div>

            <div className="firmware-dialog-warning" role="alert">
              <strong>升级过程中请保持设备连接</strong>
              <p>请勿断开 USB 连接、关闭鼠标、退出驱动或关闭当前页面。</p>
            </div>

            {upgradeState === 'idle' && firmwareAtLatest && (
              <div className="firmware-dialog-copy">
                <p>当前已是最新版本，本次操作会重新写入同版本固件。</p>
              </div>
            )}

            {(isUpgrading || upgradeState === 'success') && (
              <div className="firmware-dialog-progress">
                {isUpgrading && (
                  <div className="firmware-handshake-status">
                    <span className="firmware-handshake-spinner" aria-hidden />
                    <span>{upgradeStage}</span>
                  </div>
                )}
                <div className="firmware-progress-list">
                  {(() => {
                    const progressPercent =
                      upgradeState === 'success'
                        ? 100
                        : getUpgradeProgressPercent(
                            progressByTarget,
                            upgradeTargets
                          );
                    return (
                      <div className="firmware-progress">
                        <div className="firmware-progress-meta">
                          <span>升级进度</span>
                          <strong>{progressPercent}%</strong>
                        </div>
                        <div
                          className="firmware-progress-track"
                          role="progressbar"
                          aria-label="设备固件升级进度"
                          aria-valuemin="0"
                          aria-valuemax="100"
                          aria-valuenow={progressPercent}
                        >
                          <span style={{ width: `${progressPercent}%` }} />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {upgradeState === 'success' && (
              <div className="firmware-handshake-success" role="status">
                <strong>版本更新成功，设备即将重启</strong>
              </div>
            )}

            {upgradeState === 'error' && (
              <div className="firmware-upgrade-error" role="alert">
                {upgradeError}
              </div>
            )}

            <div className="firmware-dialog-actions">
              {upgradeState === 'idle' ? (
                <>
                  <button type="button" className="firmware-upgrade-button secondary" onClick={closeUpgradeDialog}>
                    取消
                  </button>
                  <button type="button" className="firmware-upgrade-button" onClick={startUpgrade}>
                    确认并开始升级
                  </button>
                </>
              ) : !isUpgrading ? (
                <button type="button" className="firmware-upgrade-button" onClick={closeUpgradeDialog}>
                  关闭
                </button>
              ) : (
                <span className="firmware-dialog-lock-note">升级完成前无法关闭此窗口</span>
              )}
            </div>
          </section>
        </div>
      )}
    </main>
  );
};

export default FirmwareBinTester;
